import { Link, useParams } from 'react-router-dom';
import { news } from '@/mocks/news';
import usePageMeta from '@/hooks/usePageMeta';
import { formatDate } from '@/utils/format';

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = news.find((item) => item.id === id);
  const related = article ? news.filter((item) => item.id !== article.id).slice(0, 3) : [];

  usePageMeta(
    article ? `${article.title} - 快活影院` : '资讯详情 - 快活影院',
    article ? article.summary : '快活影院资讯详情页。',
    article ? `${article.category},${article.title},影视资讯,快活影院` : undefined,
  );

  if (!article) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-24 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading font-bold text-xl text-foreground-950">资讯内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">该篇文章可能已被移除或链接有误。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full pb-4">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <nav aria-label="面包屑" className="pt-6 text-sm text-foreground-500">
          <ol className="flex items-center gap-1.5 flex-wrap">
            <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li><Link to="/news" className="hover:text-primary-600 transition-colors">资讯</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li className="text-foreground-900 truncate max-w-[60vw]">{article.title}</li>
          </ol>
        </nav>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8 lg:gap-10">
          <article className="min-w-0">
            <span className="inline-block px-2.5 py-1 rounded-md bg-accent-100 text-accent-900 text-xs">
              {article.category}
            </span>
            <h1 className="mt-3 font-heading font-extrabold text-2xl md:text-3xl leading-snug text-foreground-950">
              {article.title}
            </h1>
            <div className="mt-3 flex items-center gap-3 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span>·</span>
              <time dateTime={article.date}>{formatDate(article.date)}</time>
            </div>

            <div className="mt-6 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
              <img
                src={article.cover}
                alt={`${article.title}配图`}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {article.paragraphs.map((paragraph, i) => (
                <p key={i} className="text-[15px] leading-8 text-foreground-800">
                  {paragraph}
                </p>
              ))}
            </div>

            <p className="mt-8 pt-6 border-t border-background-200 text-xs text-foreground-400">
              * 本文为快活影院演示站点的示例内容，所有人物、机构与数据均为虚构，仅供展示页面结构。
            </p>

            <Link
              to="/news"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回资讯列表
            </Link>
          </article>

          <aside className="space-y-6 lg:sticky lg:top-24 h-fit">
            <div className="rounded-lg border border-background-200 bg-background-100 p-4">
              <h2 className="font-heading font-bold text-base text-foreground-950 mb-3">相关阅读</h2>
              <div className="space-y-4">
                {related.map((item) => (
                  <Link key={item.id} to={`/news/${item.id}`} className="group flex gap-3">
                    <span className="w-20 h-14 rounded-md overflow-hidden bg-background-200 shrink-0">
                      <img src={item.cover} alt={item.title} className="w-full h-full object-cover object-top" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {item.title}
                      </span>
                      <span className="block mt-1 text-xs text-foreground-400">{formatDate(item.date)}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-4">
              <h2 className="font-heading font-bold text-base text-foreground-950 mb-3">继续浏览</h2>
              <div className="space-y-2">
                <Link to="/category/movie" className="flex items-center justify-between px-3 py-2.5 rounded-md bg-background-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors">
                  电影频道
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
                <Link to="/category/tv" className="flex items-center justify-between px-3 py-2.5 rounded-md bg-background-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors">
                  电视剧频道
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}